export type JsonParseError = {
  message: string;
  /** Character offset of the error in the source, when derivable from the
   * engine's message; null when the engine gave no location. */
  position: number | null;
};

// JSON.parse error messages carry the location in engine-specific wording:
// V8 "... in JSON at position 25 (line 2 column 24)", Firefox "... at line 1
// column 5 of the JSON data". Same extraction approach as @codemirror/lang-json's
// jsonParseLinter.
const errorPosition = (message: string, value: string): number | null => {
  const positionMatch = message.match(/at position (\d+)/);
  if (positionMatch) {
    return Math.min(Number(positionMatch[1]), value.length);
  }

  const lineColumnMatch = message.match(/line (\d+) column (\d+)/);
  if (lineColumnMatch) {
    const lines = value.split("\n");
    const line = Math.min(Number(lineColumnMatch[1]), lines.length);
    const lineStart = lines
      .slice(0, line - 1)
      .reduce((offset, text) => offset + text.length + 1, 0);

    return Math.min(lineStart + Number(lineColumnMatch[2]) - 1, value.length);
  }

  if (message.includes("end of JSON input")) {
    return value.length;
  }

  return null;
};

export const jsonParseError = (value: string): JsonParseError | null => {
  try {
    JSON.parse(value);

    return null;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid JSON";

    return { message, position: errorPosition(message, value) };
  }
};

export const isJsonString = (value: string | null | undefined): boolean => {
  if (!value) return false;

  return jsonParseError(value) === null;
};
